import React, { Suspense, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { Environment, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { getModelPath } from "../constants/paths";
import { CAMERA, LIGHTS, MATERIAL, GROUP_POSITION, MINI_VIEWER_SCALE } from "../constants/viewerConfig";

function Part({ url, color }) {
  const { scene } = useGLTF(url);

  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(color),
          roughness: MATERIAL.roughness,
          metalness: MATERIAL.metalness,
        });
      }
    });
  }, [color, scene]);

  return <primitive object={scene} scale={MINI_VIEWER_SCALE} />;
}

function LoadingPlaceholder() {
  return (
    <div style={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)",
      borderRadius: "8px"
    }}>
      <div style={{
        width: "24px",
        height: "24px",
        border: "2px solid #e0e0e0",
        borderTopColor: "#999",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }} />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default function MiniLampViewer({ baseNumber, shadeNumber, baseColor, shadeColor }) {
  const groupRef = useRef();

  const baseUrl = getModelPath('base', baseNumber);
  const shadeUrl = getModelPath('shade', shadeNumber);

  return (
    <div style={{ width: "100%", height: "100%", minHeight: "120px" }}>
      <Canvas
        camera={{ position: CAMERA.position, fov: CAMERA.fov }}
        gl={{
          physicallyCorrectLights: true,
          colorSpace: THREE.SRGBColorSpace,
          failIfMajorPerformanceCaveat: false,
          preserveDrawingBuffer: true
        }}
        style={{ borderRadius: "8px" }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={LIGHTS.ambient.intensity} />
          {LIGHTS.directional.map((light, i) => (
            <directionalLight key={i} position={light.position} intensity={light.intensity} />
          ))}

          <group ref={groupRef} position={GROUP_POSITION}>
            <Part url={baseUrl} color={baseColor} />
            <Part url={shadeUrl} color={shadeColor} />
          </group>

          <Environment preset="city" />
        </Suspense>
      </Canvas>
    </div>
  );
}
